import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/components/LanguageProvider";
import { useState, useEffect } from "react";

export default function Downloads() {
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/downloads`)
      .then(res => res.json())
      .then(data => {
        setFiles(data.files || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching downloads:', err);
        setLoading(false);
      });
  }, []);

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (filename) => {
    if (filename.includes('wordpress')) return 'fa-wordpress';
    if (filename.includes('react')) return 'fa-react';
    return 'fa-file-zipper';
  };

  const getFileDescription = (filename) => {
    if (filename.includes('wordpress')) {
      return 'Complete WordPress theme with dark mode, custom widgets, and Palantir-inspired design.';
    }
    if (filename.includes('react')) {
      return 'Full React application source code with all components, pages, and styling.';
    }
    return 'Download file';
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold">{t("downloads") || "Downloads"}</h1>
        <p className="text-muted-foreground">
          {t("downloadsDescription") || "Download the source code and themes for DataForge Analytics."}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <i className="fa-solid fa-spinner fa-spin text-2xl text-muted-foreground"></i>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {files.map((file, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <i className={`fa-brands ${getFileIcon(file.name)} text-xl text-primary`}></i>
                    </div>
                    <div>
                      <CardTitle className="text-lg">{file.name}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {formatSize(file.size)}
                        </Badge>
                        <span className="text-xs">ZIP Archive</span>
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground mb-4">
                  {getFileDescription(file.name)}
                </p>
                <Button asChild className="w-full">
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`} 
                    download={file.name}
                  >
                    <i className="fa-solid fa-download mr-2"></i>
                    {t("download") || "Download"}
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-card/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <i className="fa-solid fa-circle-info text-primary"></i>
            {t("directLinks") || "Direct Download Links"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-brands fa-wordpress text-muted-foreground w-5"></i>
              <span className="text-muted-foreground">WordPress Theme:</span>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/dataforge-wordpress-theme.zip`}
                className="text-primary hover:underline break-all"
              >
                {process.env.REACT_APP_BACKEND_URL}/api/downloads/dataforge-wordpress-theme.zip
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-brands fa-react text-muted-foreground w-5"></i>
              <span className="text-muted-foreground">React App:</span>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/dataforge-react-app.zip`}
                className="text-primary hover:underline break-all"
              >
                {process.env.REACT_APP_BACKEND_URL}/api/downloads/dataforge-react-app.zip
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
